import styled, { css } from "styled-components";
import { fontSizes, fontWeights, spacing, typographyMixin, rawColors, borderRadius } from "@magicbi/ui-library";
import type { CommandListIconTone } from "./CommandList.types";

export const List = styled.div`
  display: grid;
  gap: 0;
`;

export const Group = styled.div`
  display: grid;
  gap: 0;
`;

/** GAP: 10px/700 with .1em tracking has no token; overline 4 is the 10px step, weight set to bold. */
export const GroupLabel = styled.div`
  display: flex;
  align-items: center;
  padding: ${spacing[2.5]} ${spacing[3]} ${spacing[1]};
  ${typographyMixin("overline", 4)}
  font-weight: ${fontWeights.bold};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

/** Keeps the heading's caps and tracking off the badge. */
export const GroupBadge = styled.span`
  display: inline-flex;
  margin-left: ${spacing[2]};
  letter-spacing: 0;
  text-transform: none;
`;

const CHIP_TONES: Record<CommandListIconTone, ReturnType<typeof css>> = {
  solid: css`
    color: ${({ theme }) => theme.colors.text.inverse};
    background: ${({ theme }) => theme.colors.primary[500]};
  `,
  neutral: css`
    color: ${rawColors.text.strongEmphasis};
    background: ${({ theme }) => theme.colors.surface.tertiary};
  `,
  brand: css`
    color: ${({ theme }) => theme.colors.primary[500]};
    background: ${({ theme }) => theme.colors.primary[100]};
  `,
  info: css`
    color: ${rawColors.accent.infoBlue.solid};
    background: ${rawColors.accent.infoBlue.bg};
  `,
  cross: css`
    color: ${rawColors.accent.cross.text};
    background: ${rawColors.accent.cross.bg};
  `,
};

/** GAP: 26px square has no size token (spacing has 24 and 28); 1.625rem is used. */
export const IconChip = styled.span<{ $tone: CommandListIconTone }>`
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 1.625rem;
  height: 1.625rem;
  border-radius: ${borderRadius.lg};
  ${typographyMixin("overline", 4)}
  font-weight: ${fontWeights.bold};
  ${({ $tone }) => CHIP_TONES[$tone]}
`;

/** The active row's chip is filled with the brand colour and a white glyph. */
const ACTIVE_CHIP = css`
  ${IconChip} {
    color: ${({ theme }) => theme.colors.text.inverse};
    background: ${({ theme }) => theme.colors.primary[500]};
  }
`;

export const Option = styled.div<{ $active: boolean; $disabled: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  height: ${spacing[10]};
  padding: 0 ${spacing[3]};
  border-radius: ${borderRadius[10]};
  color: ${({ theme, $disabled }) => ($disabled ? rawColors.text.subtleEmphasis : theme.colors.text.primary)};
  background: ${({ theme, $active, $disabled }) => ($active && !$disabled ? theme.colors.primary[100] : "transparent")};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  ${typographyMixin("body", 4)}
  font-size: ${fontSizes["13"]};
  scroll-margin: ${spacing[2]};
  ${({ $active, $disabled }) => $active && !$disabled && ACTIVE_CHIP}
  ${({ $disabled, theme }) =>
    $disabled &&
    css`
      ${IconChip} {
        color: ${rawColors.text.subtleEmphasis};
        background: ${theme.colors.surface.secondary};
      }
    `}
`;

export const Label = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** Bold lead of an "action · parameter" row. */
export const Strong = styled.span`
  font-weight: ${fontWeights.bold};
`;

/** The muted parameter after the lead. */
export const Detail = styled.span`
  color: ${rawColors.text.mediumEmphasis};
`;

export const Hint = styled.span`
  flex: none;
  max-width: 50%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${typographyMixin("caption", 4)}
  font-size: ${fontSizes["11"]};
  color: ${rawColors.text.lowEmphasis};
`;

/** "Coming soon" strip: a heading and inert pills, set off by a rule. */
export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[1.5]};
  margin: ${spacing[2.5]} ${spacing[2.5]} ${spacing[0.5]};
  padding-top: ${spacing[3]};
  border-top: 1px solid ${({ theme }) => theme.colors.border.stroke};
`;

export const ChipsLabel = styled.span`
  margin-right: ${spacing[1]};
  font-size: ${fontSizes["11"]};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  height: 1.5rem;
  padding: 0 ${spacing[2]};
  border-radius: ${borderRadius.full};
  ${typographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
  background: ${({ theme }) => theme.colors.surface.secondary};
`;

export const End = styled.span`
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: ${spacing[2]};
  margin-left: auto;
`;

export const Empty = styled.p`
  margin: 0;
  padding: ${spacing[4]} ${spacing[2]};
  text-align: center;
  ${typographyMixin("body", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;
